import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { ShieldCheck, ShieldX, Clock, CheckCircle, XCircle, Loader2, RefreshCw, FileImage, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StatusPill = ({ status }) => {
  const map = {
    approved: { className: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle, label: 'Approved' },
    pending:  { className: 'bg-amber-100 text-amber-800 border-amber-300',       Icon: Clock,       label: 'Pending' },
    rejected: { className: 'bg-red-100 text-red-800 border-red-300',             Icon: XCircle,     label: 'Rejected' },
  }[status] || { className: 'bg-gray-100 text-gray-700 border-gray-300', Icon: Clock, label: status };
  const { Icon } = map;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${map.className}`}>
      <Icon className="h-3 w-3" /> {map.label}
    </span>
  );
};


const DocReviewRow = ({ doc, onDecide }) => {
  const ocr = doc.ai_ocr?.extracted || {};
  const crossCheck = doc.ai_ocr?.cross_check || {};
  const tamperScore = doc.ai_tamper?.suspicion_score ?? null;
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const approve = async () => {
    setSaving(true);
    try {
      await onDecide(doc.id, 'approved');
    } finally { setSaving(false); }
  };

  const reject = async () => {
    setSaving(true);
    try {
      await onDecide(doc.id, 'rejected', reason || 'Document not clear enough');
      setRejectDialogOpen(false);
      setReason('');
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid={`verif-row-${doc.id}`} className="border-l-4 border-l-blue-500">
      <CardContent className="p-4 grid md:grid-cols-[1fr_1fr_auto] gap-4">
        {/* Left - user info */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold" data-testid={`verif-user-${doc.id}`}>{doc.user?.name || 'Unknown'}</span>
            <StatusPill status={doc.status} />
          </div>
          <div className="text-xs text-gray-500 space-y-0.5">
            <div>{doc.user?.email}</div>
            <div>{doc.user?.phone}</div>
            <div className="flex gap-2 items-center pt-1">
              <Badge variant="outline">{doc.user?.country || '-'}</Badge>
              <Badge variant="outline">{doc.user?.seller_type || 'individual'}</Badge>
            </div>
            <div className="pt-1">Doc type: <b>{doc.doc_type}</b></div>
            <div>Uploaded: {new Date(doc.uploaded_at).toLocaleString()}</div>
          </div>
        </div>

        {/* Middle - AI extraction */}
        <div className="text-xs space-y-1 bg-gray-50 p-2 rounded">
          <div className="font-semibold text-gray-800 flex items-center gap-1"><FileImage className="h-3 w-3" /> AI extraction</div>
          <div><b>Detected type:</b> {doc.ai_ocr?.detected_type || '-'}</div>
          {ocr.dl_number && <div><b>DL number:</b> {ocr.dl_number}</div>}
          {ocr.nic_number && <div><b>NIC number:</b> {ocr.nic_number}</div>}
          {ocr.likely_dob && <div><b>DOB:</b> {ocr.likely_dob}</div>}
          {ocr.name_candidates?.length > 0 && <div><b>Names:</b> {ocr.name_candidates.join(', ')}</div>}
          <div className="pt-1 border-t mt-1">
            <b>Name match:</b> {crossCheck.name_match ? '✓' : '✗'} · <b>DOB match:</b> {crossCheck.dob_match ? '✓' : '✗'} · <b>Confidence:</b> {((crossCheck.confidence || 0) * 100).toFixed(0)}%
          </div>
          <div>
            <b>Tamper suspicion:</b>{' '}
            <span className={tamperScore >= 0.5 ? 'text-red-600 font-semibold' : 'text-emerald-700'}>
              {tamperScore !== null ? `${(tamperScore * 100).toFixed(0)}%` : '-'}
            </span>
          </div>
          {crossCheck.warnings?.length > 0 && (
            <div className="text-amber-700 pt-1">⚠ {crossCheck.warnings.join('; ')}</div>
          )}
        </div>

        {/* Right - actions */}
        <div className="flex flex-col gap-2 min-w-[140px]">
          {doc.status === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={approve}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid={`verif-approve-${doc.id}`}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />} Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setRejectDialogOpen(true)}
                disabled={saving}
                data-testid={`verif-reject-${doc.id}`}
              >
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
            </>
          )}
          {doc.status === 'approved' && (
            <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Approved</span>
          )}
          {doc.status === 'rejected' && (
            <div className="text-xs text-red-700">
              <div className="flex items-center gap-1"><XCircle className="h-4 w-4" /> Rejected</div>
              <div className="mt-1 opacity-75">{doc.rejection_reason}</div>
            </div>
          )}
        </div>
      </CardContent>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent data-testid={`reject-dialog-${doc.id}`}>
          <DialogHeader>
            <DialogTitle>Reject document</DialogTitle>
            <DialogDescription>Explain the reason - the seller will see this and can re-upload.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Photo is too blurry, DOB not visible…"
            data-testid={`reject-reason-input-${doc.id}`}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} disabled={saving} data-testid={`reject-confirm-${doc.id}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-1" />} Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};


export default function AdminVerifications() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    try {
      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/trust/admin/documents`, {
          headers: { Authorization: `Bearer ${token}` },
          params: filter !== 'all' ? { status_filter: filter } : undefined,
        }),
        axios.get(`${API}/api/trust/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setDocs(docsRes.data.documents || []);
      setStats(statsRes.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/admin/login');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [filter]);

  const decide = async (docId, decision, reason) => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
    try {
      const res = await axios.patch(`${API}/api/trust/admin/documents/${docId}`, { decision, reason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${decision === 'approved' ? 'Approved' : 'Rejected'} - seller trust score now ${res.data.new_trust_score}`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decision failed');
    }
  };

  const filtered = docs.filter((d) => {
    if (!search) return true;
    const hay = `${d.user?.name || ''} ${d.user?.email || ''} ${d.user?.phone || ''} ${d.doc_type || ''}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-verifications-page">
      <div className="max-w-6xl mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="admin-verif-title">
              <ShieldCheck className="h-7 w-7 text-blue-600" /> Verification Queue
            </h1>
            <p className="text-gray-600 mt-1">Review AI-scanned IDs and approve or reject to influence sellers' trust scores.</p>
          </div>
          <Button onClick={fetchAll} variant="outline" data-testid="verif-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card data-testid="stat-pending"><CardContent className="p-4"><div className="text-xs text-gray-500">Pending</div><div className="text-2xl font-bold text-amber-600">{stats.documents.pending}</div></CardContent></Card>
            <Card data-testid="stat-approved"><CardContent className="p-4"><div className="text-xs text-gray-500">Approved</div><div className="text-2xl font-bold text-emerald-600">{stats.documents.approved}</div></CardContent></Card>
            <Card data-testid="stat-rejected"><CardContent className="p-4"><div className="text-xs text-gray-500">Rejected</div><div className="text-2xl font-bold text-red-600">{stats.documents.rejected}</div></CardContent></Card>
            <Card data-testid="stat-blocked"><CardContent className="p-4"><div className="text-xs text-gray-500">AML-Blocked users</div><div className="text-2xl font-bold text-red-700 flex items-center gap-1"><ShieldX className="h-5 w-5" />{stats.users_by_tier.BLOCKED}</div></CardContent></Card>
          </div>
        )}

        {/* Tier snapshot */}
        {stats && (
          <Card data-testid="tier-snapshot">
            <CardHeader><CardTitle className="text-sm">Seller trust tiers</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-3 gap-3 text-center">
              <div><div className="text-xs text-gray-500">Gold</div><div className="text-xl font-bold text-amber-500">{stats.users_by_tier.GOLD}</div></div>
              <div><div className="text-xs text-gray-500">Verified</div><div className="text-xl font-bold text-blue-600">{stats.users_by_tier.VERIFIED}</div></div>
              <div><div className="text-xs text-gray-500">Unverified</div><div className="text-xl font-bold text-gray-500">{stats.users_by_tier.UNVERIFIED}</div></div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            {['pending', 'approved', 'rejected', 'all'].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={filter === s ? 'default' : 'outline'}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </Button>
            ))}
            <Input
              placeholder="Search name / email / phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ml-auto max-w-xs"
              data-testid="verif-search-input"
            />
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-gray-500" data-testid="verif-empty">No documents match this filter.</CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="verif-list">
            {filtered.map((doc) => (
              <DocReviewRow key={doc.id} doc={doc} onDecide={decide} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
